import { t, useLang, type Lang, type StringTable } from '@/lib/i18n';

const STRINGS = {
  'how.eyebrow': { en: 'How it works', fa: 'چگونه کار می‌کند' },
  'how.title.lead': {
    en: 'Three movements between a question and an',
    fa: 'سه گام میان یک پرسش و',
  },
  'how.title.accent': {
    en: 'answer worth signing',
    fa: 'پاسخی که ارزشِ امضا دارد',
  },
  'how.title.tail': { en: '.', fa: '.' },
} satisfies StringTable<string>;

type Step = {
  label: { en: string; fa: string };
  title: { en: string; fa: string };
  description: { en: string; fa: string };
};

const steps: Step[] = [
  {
    label: { en: '01 / INGEST', fa: '۰۱ / ورود' },
    title: {
      en: 'Bring the corpus.',
      fa: 'پیکره را وارد کنید.',
    },
    description: {
      en: 'Connect documents, cables, dossiers, structured feeds. The pipeline extracts entities, relationships, and events into a versioned graph you can audit, edit, and re-ingest. Probabilistic resolution leaves merges to the analyst — no entity is silently collapsed.',
      fa: 'اسناد، تلگرام‌ها، پرونده‌ها و خوراک‌های ساخت‌یافته را پیوند بزنید. خط لولهٔ پردازش، موجودیت‌ها، روابط و رویدادها را در یک گرافِ نسخه‌بندی‌شده استخراج می‌کند که قابلِ ممیّزی، ویرایش و ورودِ دوباره است. تطبیقِ احتمالی، ادغام را به تحلیل‌گر می‌سپارد — هیچ موجودیتی در سکوت یکی نمی‌شود.',
    },
  },
  {
    label: { en: '02 / QUERY', fa: '۰۲ / پرس‌وجو' },
    title: {
      en: 'Ask in either language.',
      fa: 'به هر دو زبان بپرسید.',
    },
    description: {
      en: 'Type a question, a hypothesis, a name. The platform walks the graph — neighborhoods, paths, time windows — to assemble passages and context the way an analyst actually reads. Reasoning, entities, and retrieval all stream live.',
      fa: 'یک پرسش، یک فرضیه، یک نام بنویسید. سامانه در گراف حرکت می‌کند — همسایگی‌ها، مسیرها، پنجره‌های زمانی — تا گزاره‌ها و بافتار را به همان شیوه‌ای که یک تحلیل‌گر می‌خواند گرد آورد. استدلال، موجودیت‌ها و بازیابی، همگی به‌صورت زنده جاری‌اند.',
    },
  },
  {
    label: { en: '03 / DEFEND', fa: '۰۳ / دفاع' },
    title: {
      en: 'Read, verify, sign your name to it.',
      fa: 'بخوانید، بازبینی کنید، نامِ خود را پای آن بگذارید.',
    },
    description: {
      en: 'You receive a streamed, sectioned answer. Each section carries a calibrated confidence meter. Each claim is an inline citation back to source. Notes and corrections live in a collaborative layer beside the synthesis — your reasoning becomes part of the archive.',
      fa: 'پاسخی جاری و بخش‌بندی‌شده دریافت می‌کنید. هر بخش، نمایش‌گرِ اطمینانِ درجه‌بندی‌شده دارد. هر ادعا، ارجاعی درون‌متنی به منبع است. یادداشت‌ها و اصلاحات، در لایهٔ مشارکتی کنارِ هم‌نهاد قرار می‌گیرند — استدلالِ شما خود بخشی از آرشیو می‌شود.',
    },
  },
];

export function HowItWorks() {
  const lang = useLang();
  const pick = (s: { en: string; fa: string }, l: Lang) => s[l];

  return (
    <section
      id="how"
      className="border-t border-[var(--color-border)] bg-[var(--color-surface)]"
    >
      <div className="mx-auto max-w-[88rem] px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mb-4 text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {t(STRINGS, 'how.eyebrow', lang)}
          </div>
          <h2 className="text-3xl leading-tight md:text-5xl">
            {t(STRINGS, 'how.title.lead', lang)}{' '}
            <span className="text-[var(--color-accent)]">
              {t(STRINGS, 'how.title.accent', lang)}
            </span>
            {t(STRINGS, 'how.title.tail', lang)}
          </h2>
        </div>

        <ol className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8">
          {steps.map((step, i) => (
            <li
              key={pick(step.label, 'en')}
              className="glass-card relative flex flex-col rounded-md p-6"
            >
              <div className="mb-6 font-mono text-xxs uppercase tracking-wider text-[var(--color-accent-bright)]">
                {pick(step.label, lang)}
              </div>
              <h3 className="text-2xl leading-snug">{pick(step.title, lang)}</h3>
              <p className="mt-3 text-sm text-[var(--color-ink-secondary)]">
                {pick(step.description, lang)}
              </p>
              {i < steps.length - 1 && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -end-4 top-1/2 hidden h-px w-8 bg-[var(--color-border)] md:block"
                />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
